import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { GlobalStyles } from "@mui/material";
import {
  ACTIONS,
  defaultLocale,
  EventData,
  EVENTS,
  Joyride,
  STATUS,
  Step,
  TooltipRenderProps,
} from "react-joyride";
import useTour from "hooks/tours/useTour";

const TOUR_PRIMARY_COLOR = "#0094C6";
const TOUR_TEXT_SECONDARY_COLOR = "#495361";
const TOUR_Z_INDEX = 1400;

const tourDropdownStyles = (
  <GlobalStyles
    styles={{
      ".MuiAutocomplete-popper, .MuiPopover-root": { zIndex: `${TOUR_Z_INDEX + 1} !important` },
    }}
  />
);

type TourTooltipProps = TooltipRenderProps & {
  overallIndex: number;
  overallSize: number;
};

function TourTooltip({
  index,
  overallIndex,
  overallSize,
  step,
  isLastStep,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}: TourTooltipProps) {
  const showBack = step.buttons.includes("back") && index > 0;
  const showSkip = step.buttons.includes("skip");
  const showClose = step.buttons.includes("close");
  const showPrimary = step.buttons.includes("primary");
  const locale = { ...defaultLocale, ...step.locale };
  const primaryLabel = isLastStep ? locale.last : locale.next;
  const requiredValueSelector: string | undefined = step.data?.requireValue;
  const [hasRequiredValue, setHasRequiredValue] = useState(true);

  useEffect(() => {
    if (!requiredValueSelector) {
      setHasRequiredValue(true);
      return undefined;
    }
    const check = () => {
      const input = document.querySelector<HTMLInputElement>(requiredValueSelector);
      // Only blocks Next while the input is on screen and empty.
      setHasRequiredValue(!input || !!input.value.trim());
    };
    check();
    document.addEventListener("input", check, true);
    return () => document.removeEventListener("input", check, true);
  }, [requiredValueSelector]);

  return (
    <div
      {...tooltipProps}
      style={{
        background: "#ffffff",
        borderRadius: 4,
        fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
        maxWidth: 380,
        padding: 20,
        position: "relative",
      }}
    >
      {showClose && (
        <button
          {...closeProps}
          aria-label="Close"
          style={{
            background: "none",
            border: "none",
            color: TOUR_TEXT_SECONDARY_COLOR,
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
            position: "absolute",
            right: 12,
            top: 12,
          }}
        >
          ×
        </button>
      )}
      <div
        style={{
          color: TOUR_TEXT_SECONDARY_COLOR,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.4,
          marginBottom: 8,
          paddingRight: 24,
          textTransform: "uppercase",
        }}
      >
        Step {overallIndex + 1} of {overallSize}
      </div>
      {step.title && (
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{step.title}</div>
      )}
      <div style={{ fontSize: 14 }}>{step.content}</div>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 20,
        }}
      >
        <div style={{ alignItems: "center", display: "flex" }}>
          {showBack && (
            <button
              {...backProps}
              style={{
                background: "none",
                border: "none",
                color: TOUR_TEXT_SECONDARY_COLOR,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                marginRight: 8,
              }}
            >
              {locale.back}
            </button>
          )}
          {showSkip && (
            <button
              {...skipProps}
              style={{
                background: "none",
                border: "none",
                color: TOUR_TEXT_SECONDARY_COLOR,
                cursor: "pointer",
                fontSize: 13,
                marginRight: 8,
              }}
            >
              {locale.skip}
            </button>
          )}
          {showPrimary && (
            <button
              {...primaryProps}
              disabled={!hasRequiredValue}
              style={{
                background: TOUR_PRIMARY_COLOR,
                border: "none",
                borderRadius: 4,
                color: "#ffffff",
                cursor: hasRequiredValue ? "pointer" : "not-allowed",
                opacity: hasRequiredValue ? 1 : 0.5,
                fontSize: 14,
                fontWeight: 600,
                padding: "8px 18px",
              }}
            >
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface PageTourProps {
  tourKey: string;
  steps: Step[];
  onFinish?: () => void;
  onSkip?: () => void;
  stepOffset?: number;
  totalSteps?: number;
  onStepChange?: (step: Step) => void;
}

export interface PageTourHandle {
  replay: (start?: number | string) => void;
  next: () => void;
  advanceFrom: (stepId: string) => void;
}

const PageTour = forwardRef<PageTourHandle, PageTourProps>(function PageTour(
  { tourKey, steps, onFinish, onSkip, stepOffset = 0, totalSteps, onStepChange },
  ref
) {
  const { run, onEvent, replay } = useTour(tourKey);
  const [stepIndex, setStepIndex] = useState(0);
  const overallSize = totalSteps ?? steps.length;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const runRef = useRef(run);
  runRef.current = run;

  useImperativeHandle(
    ref,
    () => ({
      replay: (start) => {
        let index = 0;
        if (typeof start === "string") {
          index = stepsRef.current.findIndex((step) => step.id === start);
        } else if (typeof start === "number") {
          index = start;
        }
        setStepIndex(Math.max(0, index));
        replay();
      },
      next: () => setStepIndex((index) => index + 1),
      advanceFrom: (stepId: string) => {
        if (!runRef.current) return;
        setStepIndex((index) => (stepsRef.current[index]?.id === stepId ? index + 1 : index));
      },
    }),
    [replay]
  );

  const stepsWithoutBeacon = steps.map((step) => ({ skipBeacon: true, ...step }));

  const joyrideStyles = {
    options: {
      //arrowColor: "#ffffff",
      //backgroundColor: "#e10c0c",
      //overlayColor: "rgba(138, 39, 39, 0.5)",
      primaryColor: "#0094C6",
      fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
      //textColor: "#2b9e17",
      //zIndex: 1400,
    },
    tooltip: {
      borderRadius: 4,
      padding: 20,
    },
    tooltipTitle: {
      fontSize: 16,
      fontWeight: 700,
      margin: 0,
      marginBottom: 8,
    },
    tooltipContent: {
      fontSize: 14,
      padding: 8,
    },
    buttonPrimary: {
      backgroundColor: "#0094C6",
      borderRadius: 4,
      fontSize: 16,
      fontWeight: 600,
      padding: "8px 18px",
    },
    buttonBack: {
      color: "#495361",
      fontSize: 13,
      fontWeight: 600,
      marginRight: 8,
    },
    buttonSkip: {
      color: "#495361",
      fontSize: 13,
    },
    buttonClose: {
      color: "#495361",
    },
  };

  const handleEvent = (data: EventData) => {
    onEvent(data);
    if (data.type === EVENTS.TARGET_NOT_FOUND) {
      if (data.action === ACTIONS.PREV) setStepIndex((index) => Math.max(0, index - 1));
      else setStepIndex((index) => index + 1);
    } else if (data.type === EVENTS.STEP_AFTER) {
      if (data.action === ACTIONS.NEXT) setStepIndex((index) => index + 1);
      else if (data.action === ACTIONS.PREV) setStepIndex((index) => Math.max(0, index - 1));
    }
    if (data.type === EVENTS.TOOLTIP) {
      onStepChange?.(data.step);
    }
    if (data.status === STATUS.SKIPPED) {
      (onSkip ?? onFinish)?.();
    } else if (data.status === STATUS.FINISHED) {
      onFinish?.();
    }
  };

  const renderTooltip = (props: TooltipRenderProps) => (
    <TourTooltip {...props} overallIndex={stepOffset + props.index} overallSize={overallSize} />
  );

  return (
    <>
      {run && tourDropdownStyles}
      <Joyride
        steps={stepsWithoutBeacon}
        run={run}
        stepIndex={stepIndex}
        continuous
        options={{ buttons: ["back", "close", "primary"], zIndex: TOUR_Z_INDEX }}
        styles={joyrideStyles}
        tooltipComponent={renderTooltip}
        onEvent={handleEvent}
      />
    </>
  );
});

export default PageTour;
